// apps/roleforge-app/src/components/chat/StepIndicator.tsx
// Display-only progress indicator for the audit experience. Step transitions
// are driven by audit.status — these chips are visual breadcrumbs, not
// navigation.

interface Step {
  label: string;
  subtitle: string;
}

const STEPS: Step[] = [
  { label: 'Discovery', subtitle: 'Conversation' },
  { label: 'Map', subtitle: 'AI Workforce Blueprint' },
  { label: 'Findings', subtitle: 'Opportunity matrix' },
  { label: 'Materialize', subtitle: 'Set it up in VibeControl' },
];

interface Props {
  currentStep: 1 | 2 | 3 | 4;
  orientation?: 'horizontal' | 'vertical';
}

export function StepIndicator({ currentStep, orientation = 'horizontal' }: Props) {
  const isVertical = orientation === 'vertical';
  const navClass = isVertical ? 'flex flex-col gap-1' : 'flex gap-2 flex-wrap';

  return (
    <nav aria-label="Blueprint steps" className={navClass}>
      {STEPS.map((step, idx) => {
        const stepNum = (idx + 1) as 1 | 2 | 3 | 4;
        const isActive = stepNum === currentStep;
        const isPassed = stepNum < currentStep;

        if (isVertical) {
          const rowClass = 'flex items-start gap-3 rounded-xl px-3 py-2.5';

          let circleClass =
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold ';
          let labelClass = 'text-sm font-semibold leading-tight ';
          const subtitleClass = 'mt-0.5 text-[11px] leading-tight text-slate-500';

          if (isActive) {
            circleClass += 'border-coral text-coral';
            labelClass += 'text-coral';
          } else if (isPassed) {
            circleClass += 'border-mint/60 bg-mint/10 text-mint';
            labelClass += 'text-mint';
          } else {
            circleClass += 'border-slate-700 text-slate-500';
            labelClass += 'text-slate-400';
          }

          return (
            <div key={step.label} className={rowClass} data-testid={`audit-step-${stepNum}`}>
              <span aria-hidden="true" className={circleClass}>
                {isPassed ? '✓' : stepNum}
              </span>
              <div className="min-w-0 flex-1">
                <div className={labelClass}>{step.label}</div>
                <div className={subtitleClass}>{step.subtitle}</div>
              </div>
            </div>
          );
        }

        let className =
          'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium transition-colors ';

        if (isActive) {
          className += 'bg-coral text-paper';
        } else if (isPassed) {
          className += 'text-mint';
        } else {
          className += 'text-graphite';
        }

        return (
          <span key={step.label} className={className} data-testid={`audit-step-${stepNum}`}>
            {isPassed && (
              <span aria-hidden="true">✓</span>
            )}
            <span>{stepNum}. {step.label}</span>
          </span>
        );
      })}
    </nav>
  );
}
